import React, { useEffect, useState } from "react";

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Textarea,
  Select,
  Radio,
  RadioGroup,
  Checkbox,
  Stack,
  VStack,
  Text,
  Divider,
} from "@chakra-ui/react";

import {
  submitDocumentrequests,
  getLoanList,
} from "../../services/tankstackApiRequest";

import CustomToast from "./customToast";

const NewRequestModal = ({
  isOpen,
  onClose,
  service,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [loanList, setLoanList] = useState([]);
  const [isLoanLoading, setIsLoanLoading] = useState(false);

  const { addToast } = CustomToast();

  useEffect(() => {
    if (!service || !isOpen) {
      setFormData({});
      setErrors({});
      setLoanList([]);
      return;
    }

    const initialValues = {};

    const fields = service?.service_type_fields || [];

    fields.forEach((field) => {
      if (field.fieldType === "CHECKBOX") {
        initialValues[field.fieldKey] =
          field.defaultValue === true ||
          field.defaultValue === "true";

        return;
      }

      initialValues[field.fieldKey] =
        field.defaultValue ?? "";
    });

    setFormData(initialValues);
    setErrors({});
  }, [service, isOpen]);

  useEffect(() => {
    const fetchLoanList = async () => {
      if (!isOpen || !service) {
        setLoanList([]);
        return;
      }

      const hasLoanField = (
        service?.service_type_fields || []
      ).some(
        (field) =>
          field.fieldKey === "loan_number" &&
          field.fieldType === "SELECT"
      );




      if (!hasLoanField) {
        setLoanList([]);
        return;
      }
      try {
        setIsLoanLoading(true);

        const response = await getLoanList();

 
        const loans = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.data)
          ? response.data.data
          : [];
        const loanOptions = loans
          .filter(
            (loan) =>
              loan?.loan_number !== undefined &&
              loan?.loan_number !== null &&
              String(loan.loan_number).trim() !== ""
          )
          .map((loan) => ({
            value: String(loan.loan_number),
            label: String(loan.loan_number),
          }));

        setLoanList(loanOptions);
      } catch (error) {
        console.error(
          "Failed to fetch loan list:",
          error
        );
        setLoanList([]);
        addToast({
          message:
            error?.response?.data?.message ||
            error?.message ||
            "Failed to fetch loan list.",
          status: "error",
        });
      } finally {
        setIsLoanLoading(false);
      }
    };
    fetchLoanList();
  }, [service, isOpen]);

  const handleChange = (
    fieldKey,
    value,
    fieldType
  ) => {
    let parsedValue = value;

    if (fieldType === "NUMBER") {
      parsedValue =
        value === ""
          ? ""
          : Number(value);
    }

    if (
      fieldKey === "loan_number" &&
      fieldType === "SELECT"
    ) {
      parsedValue =
        value === ""
          ? ""
          : String(value);
    }

    setFormData((prev) => ({
      ...prev,
      [fieldKey]: parsedValue,
    }));

    setErrors((prev) => ({
      ...prev,
      [fieldKey]: "",
    }));
  };

  const getDropdownValues = (field) => {
    if (
      Array.isArray(field?.dropdownValues)
    ) {
      return field.dropdownValues;
    }

    return [];
  };

  const validateForm = () => {
    const newErrors = {};

    const fields =
      service?.service_type_fields || [];

    fields.forEach((field) => {
      const isVisibleField =
        !field.isHidden ||
        field.fieldKey === "loan_number";

      if (
        !isVisibleField ||
        !field.isRequired
      ) {
        return;
      }

      const value =
        formData[field.fieldKey];

      let empty = false;

      if (
        field.fieldType === "CHECKBOX"
      ) {
        empty = value !== true;
      } else if (
        field.fieldType === "FILE" ||
        field.fieldType === "IMAGE"
      ) {
        empty = !value;
      }else {
        empty =
          value === undefined ||
          value === null ||
          String(value).trim() === "";
      }

      if (empty) {
        newErrors[field.fieldKey] =
          field.fieldType === "CHECKBOX"
            ? `Please accept ${field.displayName}`
            : `${field.displayName} is required`;
      }

      if (
        !empty &&
        field.validationRule &&
        field.fieldType !== "CHECKBOX"
      ) {
        try {
          const regex =
            new RegExp(
              field.validationRule
            );

          if (
            !regex.test(
              String(value)
            )
          ) {
            newErrors[
              field.fieldKey
            ] =
              `${field.displayName} is invalid`;
          }
        } catch (error) {
          console.error(
            "Invalid validation regex:",
            field.validationRule
          );
        }
      }
    });

    setErrors(newErrors);

    return (
      Object.keys(newErrors).length === 0
    );
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {

      const payload = {
        service_type_id: Number(
          service?.id
        ),
        requested_document_format: "PDF",
        source: "WEB",
        payload: {
          ...formData,
        },
      };


      const createResponse =
        await submitDocumentrequests(
          payload
        );


      const isSuccess =
        createResponse?.success === true ||
        createResponse?.status === "success" ||
        createResponse?.statusCode === 200 ||
        createResponse?.statusCode === 201;

      if (!isSuccess) {
        throw new Error(
          createResponse?.message ||
            "Failed to submit document request."
        );
      }

      addToast({
        message:
          createResponse?.message ||
          "Document request submitted successfully.",
        status: "success",
      });

      if (onSuccess) {
        await onSuccess();
      }

      onClose();
    } catch (error) {
      console.error(
        "Document request submit error:",
        error
      );

      addToast({
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to submit document request.",
        status: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (field) => {
    const {
      fieldKey,
      displayName,
      fieldType,
      isRequired,
      placeholder,
    } = field;

    const value =
      formData[fieldKey];

    const error =
      errors[fieldKey];

    if (fieldType === "TEXT") {
      return (
        <FormControl
          key={fieldKey}
          isRequired={isRequired}
          isInvalid={!!error}
        >
          <FormLabel>
            {displayName}
          </FormLabel>

          <Input
            type="text"
            placeholder={
              placeholder ||
              `Enter ${displayName}`
            }
            value={value || ""}
            onChange={(e) =>
              handleChange(
                fieldKey,
                e.target.value,
                fieldType
              )
            }
          />

          <FormErrorMessage>
            {error}
          </FormErrorMessage>
        </FormControl>
      );
    }

    if (fieldType === "EMAIL") {
      return (
        <FormControl
          key={fieldKey}
          isRequired={isRequired}
          isInvalid={!!error}
        >
          <FormLabel>
            {displayName}
          </FormLabel>

          <Input
            type="email"
            placeholder={
              placeholder ||
              `Enter ${displayName}`
            }
            value={value || ""}
            onChange={(e) =>
              handleChange(
                fieldKey,
                e.target.value,
                fieldType
              )
            }
          />

          <FormErrorMessage>
            {error}
          </FormErrorMessage>
        </FormControl>
      );
    }

    if (fieldType === "MOBILE") {
      return (
        <FormControl
          key={fieldKey}
          isRequired={isRequired}
          isInvalid={!!error}
        >
          <FormLabel>
            {displayName}
          </FormLabel>

          <Input
            type="tel"
            placeholder={
              placeholder ||
              `Enter ${displayName}`
            }
            value={value || ""}
            onChange={(e) =>
              handleChange(
                fieldKey,
                e.target.value,
                fieldType
              )
            }
          />

          <FormErrorMessage>
            {error}
          </FormErrorMessage>
        </FormControl>
      );
    }

    if (fieldType === "NUMBER") {
      return (
        <FormControl
          key={fieldKey}
          isRequired={isRequired}
          isInvalid={!!error}
        >
          <FormLabel>
            {displayName}
          </FormLabel>

          <Input
            type="number"
            placeholder={
              placeholder ||
              `Enter ${displayName}`
            }
            value={
              value === undefined ||
              value === null
                ? ""
                : value
            }
            onChange={(e) =>
              handleChange(
                fieldKey,
                e.target.value,
                fieldType
              )
            }
          />

          <FormErrorMessage>
            {error}
          </FormErrorMessage>
        </FormControl>
      );
    }

    if (fieldType === "TEXTAREA") {
      return (
        <FormControl
          key={fieldKey}
          isRequired={isRequired}
          isInvalid={!!error}
        >
          <FormLabel>
            {displayName}
          </FormLabel>

          <Textarea
            placeholder={
              placeholder ||
              `Enter ${displayName}`
            }
            value={value || ""}
            onChange={(e) =>
              handleChange(
                fieldKey,
                e.target.value,
                fieldType
              )
            }
          />

          <FormErrorMessage>
            {error}
          </FormErrorMessage>
        </FormControl>
      );
    }

    if (fieldType === "DATE") {
      return (
        <FormControl
          key={fieldKey}
          isRequired={isRequired}
          isInvalid={!!error}
        >
          <FormLabel>
            {displayName}
          </FormLabel>

          <Input
            type="date"
            value={value || ""}
            onChange={(e) =>
              handleChange(
                fieldKey,
                e.target.value,
                fieldType
              )
            }
          />

          <FormErrorMessage>
            {error}
          </FormErrorMessage>
        </FormControl>
      );
    }

    if (fieldType === "DATETIME") {
      return (
        <FormControl
          key={fieldKey}
          isRequired={isRequired}
          isInvalid={!!error}
        >
          <FormLabel>
            {displayName}
          </FormLabel>

          <Input
            type="datetime-local"
            value={value || ""}
            onChange={(e) =>
              handleChange(
                fieldKey,
                e.target.value,
                fieldType
              )
            }
          />

          <FormErrorMessage>
            {error}
          </FormErrorMessage>
        </FormControl>
      );
    }

    if (fieldType === "TIME") {
      return (
        <FormControl
          key={fieldKey}
          isRequired={isRequired}
          isInvalid={!!error}
        >
          <FormLabel>
            {displayName}
          </FormLabel>

          <Input
            type="time"
            value={value || ""}
            onChange={(e) =>
              handleChange(
                fieldKey,
                e.target.value,
                fieldType
              )
            }
          />

          <FormErrorMessage>
            {error}
          </FormErrorMessage>
        </FormControl>
      );
    }

    if (fieldType === "SELECT") {
      const isLoanField =
        fieldKey === "loan_number";

      const values =
        isLoanField
          ? loanList
          : getDropdownValues(field);

      return (
        <FormControl
          key={fieldKey}
          isRequired={isRequired}
          isInvalid={!!error}
        >
          <FormLabel>
            {displayName}
          </FormLabel>

          <Select
            placeholder={
              placeholder ||
              `Select ${displayName}`
            }
            value={
              value === undefined ||
              value === null
                ? ""
                : value
            }
            onChange={(e) =>
              handleChange(
                fieldKey,
                e.target.value,
                fieldType
              )
            }
            isDisabled={
              isLoanField &&
              isLoanLoading
            }
          >
            {isLoanField
              ? values.map((option) => (
                  <option
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ))
              : values.map(
                  (option, index) => (
                    <option
                      key={`${fieldKey}-${index}`}
                      value={option}
                    >
                      {String(option).replace(
                        /_/g,
                        " "
                      )}
                    </option>
                  )
                )}
          </Select>

          <FormErrorMessage>
            {error}
          </FormErrorMessage>
        </FormControl>
      );
    }

    if (fieldType === "RADIO") {
      const values =
        getDropdownValues(field);

      return (
        <FormControl
          key={fieldKey}
          isRequired={isRequired}
          isInvalid={!!error}
        >
          <FormLabel>
            {displayName}
          </FormLabel>

          <RadioGroup
            value={value || ""}
            onChange={(selectedValue) =>
              handleChange(
                fieldKey,
                selectedValue,
                fieldType
              )
            }
          >
            <Stack direction="column">
              {values.map(
                (option, index) => (
                  <Radio
                    key={`${fieldKey}-${index}`}
                    value={option}
                  >
                    {String(option).replace(
                      /_/g,
                      " "
                    )}
                  </Radio>
                )
              )}
            </Stack>
          </RadioGroup>

          <FormErrorMessage>
            {error}
          </FormErrorMessage>
        </FormControl>
      );
    }

    if (fieldType === "CHECKBOX") {
      return (
        <FormControl
          key={fieldKey}
          isRequired={isRequired}
          isInvalid={!!error}
        >
          <Checkbox
            isChecked={value === true}
            onChange={(e) =>
              handleChange(
                fieldKey,
                e.target.checked,
                fieldType
              )
            }
          >
            {displayName}
          </Checkbox>

          <FormErrorMessage>
            {error}
          </FormErrorMessage>
        </FormControl>
      );
    }


    return (
      <Text
        key={fieldKey}
        color="red.500"
      >
        Unsupported field type:{" "}
        {fieldType}
      </Text>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      scrollBehavior="inside"
    >
      <ModalOverlay />

      <ModalContent>
        <ModalHeader>
          New Document Request
        </ModalHeader>

        <ModalCloseButton />

        <ModalBody>
          {service && (
            <VStack
              spacing={5}
              align="stretch"
            >
              <Text
                fontSize="lg"
                fontWeight="600"
              >
                {service?.serviceName}
              </Text>

              {service?.description && (
                <Text
                  fontSize="sm"
                  color="gray.600"
                >
                  {service.description}
                </Text>
              )}

              <Divider />

              {(
                service?.service_type_fields ||
                []
              )
                .filter((field) => {
                  if (
                    field.fieldKey ===
                    "loan_number"
                  ) {
                    return true;
                  }

                  return !field.isHidden;
                })
                .sort(
                  (a, b) =>
                    (a.displayOrder || 0) -
                    (b.displayOrder || 0)
                )
                .map((field) =>
                  renderField(field)
                )}
            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={onClose}
            isDisabled={isSubmitting}
          >
            Cancel
          </Button>

          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            loadingText="Submitting"
          >
            Submit Request
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default NewRequestModal;